import { createStyles, Tabs } from "@mantine/core";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Body, Heading } from "../../../components/Typography";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { RpiCard } from "../../../rpi-core/card/RpiCard";

const useStyles = createStyles(() => ({
  container: {
    height: "100%",
    overflowY: "auto" as const,
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
    alignItems: "flex-start",
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    padding: tokens.spacing[6],
    gap: tokens.spacing[6],
    alignSelf: "stretch",
  },
  chevron: {
    display: "inline-block",
    lineHeight: 0,
  },
  headerGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[2],
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "column" as const,
  },
  panel: {
    paddingTop: tokens.spacing[6],
  },
}));

const PAYMENT_TABS = [
  { label: "Details", value: "details" },
  { label: "Orders", value: "orders" },
] as const;

function isSuperAdminWithBraintreeId(
  isSuperAdmin: boolean,
  braintreeTransactionId: string | null | undefined
): braintreeTransactionId is string {
  return isSuperAdmin && !!braintreeTransactionId;
}

function parsePaymentId(raw: string | undefined): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  return Number(raw);
}

export const PaymentViewV2: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = React.useState<string | null>("details");

  const paymentId = parsePaymentId(id);
  const { data } = GraphQL.usePaymentQuery({ id: paymentId ?? 0 }, { enabled: paymentId !== null });
  const braintreeTransactionId = data?.payment.braintreeTransactionId;

  return (
    <div className={classes.container}>
      <Button variant="text" intent="neutral" size="small" onClick={() => navigate(getRoutePath("billing"))}>
        <span className={classes.chevron}>
          <IconV3 icon="chevron_left" size={16} />
        </span>
        Back to All Payments
      </Button>

      <RpiCard className={classes.card}>
        <div className={classes.headerGroup}>
          <Heading size="xl" weight="bold">
            {id}
          </Heading>
          {isSuperAdminWithBraintreeId(user.is.SUPER_ADMIN, braintreeTransactionId) && (
            <Body>Braintree ID# {braintreeTransactionId}</Body>
          )}
        </div>

        <Tabs value={activeTab} onTabChange={setActiveTab} className={classes.tabsContainer}>
          <Tabs.List>
            {PAYMENT_TABS.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {PAYMENT_TABS.map((tab) => (
            <Tabs.Panel key={tab.value} value={tab.value} className={classes.panel}>
              <Body>Coming soon.</Body>
            </Tabs.Panel>
          ))}
        </Tabs>
      </RpiCard>
    </div>
  );
};
